import { Eye, Loader2, Radio, XCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useCancelReplay, useReplayList } from '@/hooks/useReplays';
import {
  ACTIVE_REPLAY_STATUSES,
  REPLAY_RESULT_VARIANT,
  REPLAY_STATUS_VARIANT,
} from '@/lib/api/replays.constants';
import { formatDateTime } from '@/utils/format';

export const ReplaysListPage = () => {
  usePageTitle('Replays');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: replays, isLoading } = useReplayList();
  const cancelMutation = useCancelReplay();

  const handleCancel = async (id: string) => {
    try {
      await cancelMutation.mutateAsync(id);
      toast({ title: 'Cancelled', description: 'Replay has been cancelled.' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to cancel replay.' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Replays</CardTitle>
            <CardDescription>
              Simulated test conversations replayed from real calls. Each replay runs an AI customer
              against the agent and judges the result.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {!replays?.length ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No replays yet. Start one from a call&apos;s detail page.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Source call</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Modality</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Turns</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {replays.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatDateTime(r.created_at)}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/calls/${r.source_call_id}`}
                        className="text-primary text-xs hover:underline"
                      >
                        {r.source_call_id.slice(0, 8)}
                      </Link>
                    </TableCell>
                    <TableCell className="text-xs">{r.channel}</TableCell>
                    <TableCell className="text-xs">{r.modality}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Badge variant={REPLAY_STATUS_VARIANT[r.status]}>{r.status}</Badge>
                        {r.status === 'running' && r.modality === 'VOICE' && (
                          <Link to={`/agent-improvement/replays/${r.id}`}>
                            <Badge
                              variant="destructive"
                              className="cursor-pointer gap-1 text-[10px]"
                            >
                              <Radio className="h-3 w-3" />
                              Live
                            </Badge>
                          </Link>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {r.result ? (
                        <Badge variant={REPLAY_RESULT_VARIANT[r.result]}>{r.result}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">&mdash;</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{r.turns || '\u2014'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/agent-improvement/replays/${r.id}`)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {ACTIVE_REPLAY_STATUSES.has(r.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={cancelMutation.isPending}
                            onClick={() => void handleCancel(r.id)}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
